if (!customElements.get('datu-print-configurator-v2')) {
  customElements.define('datu-print-configurator-v2', class extends HTMLElement {
    static resolveVariant(variants, values, position) {
      return variants.filter(variant => variant.available && variant.options.every((value, index) => index > position || value === values[index]))
        .sort((a, b) => {
          const score = variant => variant.options.reduce((total, value, index) => total + (value === values[index] ? 4 ** (values.length - index) : 0), 0);
          return score(b) - score(a);
        })[0];
    }
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.variants = JSON.parse(this.querySelector('[data-datu-variants]').textContent);
      this.messages = JSON.parse(this.querySelector('[data-datu-messages]').textContent);
      this.inputs = Array.from(this.querySelectorAll('input[data-option-position]'));
      this.picker = this.closest('variant-picker');
      this.form = document.getElementById(this.dataset.formId);
      this.scope = this.closest('product-rerender');
      this.current = JSON.parse(this.picker.querySelector('[data-variant]').textContent);
      this.renderAvailability();
      this.addEventListener('change', event => this.change(event), { signal: this.controller.signal });
      this.selectInitialFrame();
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
    async selectInitialFrame() {
      const parameters = new URLSearchParams(location.search);
      if (this.picker.getAttribute('context') !== 'main_product' || parameters.has('variant') || parameters.has('option_values') || this.scope.dataset.datuDefaultApplied) return;
      const next = this.variants.find(variant => variant.available && ['12x18', '12x12'].includes(variant.options[0]) && variant.options[1] === 'Black Premium Oak' && variant.options[2] === 'Mats')
        || this.variants.find(variant => variant.available && variant.options[1] !== 'Unframed' && variant.options[2] === 'Mats');
      if (!next || next.id === this.current?.id) return;
      this.scope.dataset.datuDefaultApplied = 'true';
      const optionValues = next.options.map((value, index) => this.inputs.find(item => Number(item.dataset.optionPosition) === index + 1 && item.dataset.optionValue === value)?.value);
      if (optionValues.some(value => !value)) return;
      this.scope.inert = true;
      this.scope.setAttribute('aria-busy', 'true');
      try {
        await customElements.whenDefined('variant-picker');
        if (this.isConnected) await this.picker.selectCombination({optionValues, productChange: false});
      } catch (error) {
        this.status(this.messages.error);
      } finally {
        this.scope.inert = false;
        this.scope.removeAttribute('aria-busy');
      }
    }

    label(value, position) {
      return this.inputs.find(input => Number(input.dataset.optionPosition) === position + 1 && input.dataset.optionValue === value)?.dataset.optionLabel || value;
    }
    renderAvailability() {
      if (!this.current?.options) return;
      const values = this.current.options;
      this.inputs.forEach(input => {
        const position = Number(input.dataset.optionPosition) - 1;
        const candidates = this.variants.filter(variant => variant.options[position] === input.dataset.optionValue && variant.options.every((value, index) => index >= position || value === values[index]));
        input.disabled = !candidates.some(variant => variant.available);
        input.checked = input.dataset.optionValue === values[position];
        const note = input.nextElementSibling.querySelector('[data-datu-choice-note]');
        note.hidden = !input.disabled;
        note.textContent = candidates.length ? this.messages.sold_out : position === 1 ? this.messages.frame_unavailable : this.messages.unavailable;
      });
      const frameNotes = [];
      this.inputs.filter(input => input.dataset.optionPosition === '2' && input.disabled).forEach(input => {
        const sizes = [...new Set(this.variants.filter(variant => variant.available && variant.options[1] === input.dataset.optionValue).map(variant => this.label(variant.options[0], 0)))];
        if (sizes.length) frameNotes.push(`${input.dataset.optionLabel}: ${this.messages.sizes_available} ${sizes.join(', ')}.`);
      });
      const frameNote = this.querySelector('[data-datu-step="2"] [data-datu-compatibility]');
      frameNote.textContent = frameNotes.join(' ');
      frameNote.hidden = !frameNotes.length;
      const matNote = this.querySelector('[data-datu-step="3"] [data-datu-compatibility]');
      const matRestricted = this.inputs.some(input => input.dataset.optionPosition === '3' && input.disabled);
      matNote.textContent = values[1] === 'Unframed' ? this.messages.unframed_note : this.messages.mat_unavailable;
      matNote.hidden = !matRestricted;
      this.querySelector('[data-datu-selection-summary]').textContent = values.map((value, index) => this.label(value, index)).join(' · ');
    }
    status(message) {
      const status = this.querySelector('[data-datu-status]');
      status.hidden = !message;
      status.textContent = message;
    }
    async change(event) {
      const input = event.target.closest('input[data-option-position]');
      if (!input) return;
      event.stopPropagation();
      if (this.busy) return;
      const position = Number(input.dataset.optionPosition) - 1;
      const values = [...this.current.options];
      values[position] = input.dataset.optionValue;
      const next = this.constructor.resolveVariant(this.variants, values, position);
      if (!next || next.id === this.current.id) { this.renderAvailability(); return; }
      const adjustments = next.options.flatMap((value, index) => index > position && value !== values[index] ? [this.label(value, index)] : []);
      const message = adjustments.length ? `${this.messages.adjusted} ${adjustments.join(' · ')}.` : '';
      const oldVariant = this.current;
      this.current = next;
      this.renderAvailability();
      const optionValues = next.options.map((value, index) => this.inputs.find(item => Number(item.dataset.optionPosition) === index + 1 && item.dataset.optionValue === value).value);
      this.busy = true;
      this.status(this.messages.updating);
      this.scope.setAttribute('aria-busy', 'true');
      this.scope.setAttribute('data-datu-configuring', '');
      this.scope.inert = true;
      const submitButtons = Array.from(this.form.elements).filter(element => element.tagName === 'BUTTON' && element.type === 'submit').map(element => ({ element, disabled: element.disabled }));
      submitButtons.forEach(({ element }) => { element.disabled = true; });
      try {
        await this.picker.selectCombination({ optionValues, productChange: false });
        const replacement = this.scope.querySelector('datu-print-configurator-v2');
        replacement?.status(message);
      } catch (error) {
        this.current = oldVariant;
        this.renderAvailability();
        this.status(this.messages.error);
      } finally {
        submitButtons.forEach(({ element, disabled }) => { if (element.isConnected) element.disabled = disabled; });
        this.scope.inert = false;
        this.scope.removeAttribute('aria-busy');
        this.scope.removeAttribute('data-datu-configuring');
        this.busy = false;
        const focused = document.getElementById(input.id);
        focused?.focus({ preventScroll: true });
      }
    }
  });
}
